import { computed, signal } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { SUPPORT_ATTACHMENT_LIMITS } from '../support-system.config';
import {
  SupportAttachment,
  SupportErrorCode,
  SupportUploadEvent,
} from '../support-system.models';

export type SupportUploadStatus = 'uploading' | 'done' | 'failed';

/** A file picked for the next message, uploading or uploaded. */
export interface SupportDraftAttachment {
  localId: string;
  file: File;
  /** Object URL for image thumbnails; revoked when the draft is dropped. */
  previewUrl: string | null;
  status: SupportUploadStatus;
  /** 0–1. */
  progress: number;
  attachment?: SupportAttachment;
  /** Why the upload failed. */
  error?: SupportErrorCode;
}

export interface SupportAttachmentRejection {
  name: string;
  reason: 'type' | 'size' | 'count';
}

let nextLocalId = 0;

/**
 * The files attached to one composer. Each file starts uploading as soon as
 * it's added, so sending only has to pass the references along.
 */
export class SupportUploadQueue {
  private readonly _items = signal<SupportDraftAttachment[]>([]);
  readonly items = this._items.asReadonly();
  readonly uploading = computed(() =>
    this._items().some((i) => i.status === 'uploading'),
  );
  readonly failed = computed(() =>
    this._items().some((i) => i.status === 'failed'),
  );
  /** Everything is uploaded (or there is nothing): safe to send. */
  readonly settled = computed(() => !this.uploading() && !this.failed());
  readonly count = computed(() => this._items().length);

  private readonly uploads = new Map<string, Subscription>();

  constructor(
    private readonly upload: (file: File) => Observable<SupportUploadEvent>,
  ) {}

  /** Starts uploading the files that pass the limits; returns the others. */
  add(files: File[]): SupportAttachmentRejection[] {
    const rejected: SupportAttachmentRejection[] = [];
    const accepted: SupportDraftAttachment[] = [];
    const { maxFiles, maxBytes } = SUPPORT_ATTACHMENT_LIMITS;

    for (const file of files) {
      if (!isAllowedFile(file)) {
        rejected.push({ name: file.name, reason: 'type' });
      } else if (file.size > maxBytes) {
        rejected.push({ name: file.name, reason: 'size' });
      } else if (this._items().length + accepted.length >= maxFiles) {
        rejected.push({ name: file.name, reason: 'count' });
      } else {
        accepted.push({
          localId: `att-${++nextLocalId}`,
          file,
          previewUrl: isImageType(file.type) ? URL.createObjectURL(file) : null,
          status: 'uploading',
          progress: 0,
        });
      }
    }

    if (accepted.length) {
      this._items.update((items) => [...items, ...accepted]);
      accepted.forEach((item) => this.start(item.localId));
    }
    return rejected;
  }

  /** Starts over: a failed upload needs a fresh reservation. */
  retry(localId: string): void {
    this.patch(localId, { status: 'uploading', progress: 0, error: undefined });
    this.start(localId);
  }

  remove(localId: string): void {
    this.uploads.get(localId)?.unsubscribe();
    this.uploads.delete(localId);
    const item = this._items().find((i) => i.localId === localId);
    if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
    this._items.update((items) => items.filter((i) => i.localId !== localId));
  }

  /** The thumbnail can't be shown (e.g. a corrupt image): use the file icon. */
  dropPreview(localId: string): void {
    const item = this._items().find((i) => i.localId === localId);
    if (!item?.previewUrl) return;
    URL.revokeObjectURL(item.previewUrl);
    this.patch(localId, { previewUrl: null });
  }

  /**
   * Hands the uploaded files over to a message and empties the queue. Image
   * previews go with them (as `localUrl`), so the bubble shows them at once.
   */
  take(): SupportAttachment[] {
    const taken = this._items().filter((i) => i.status === 'done' && i.attachment);
    const attachments = taken.map((i) => ({
      ...i.attachment!,
      localUrl: i.previewUrl || undefined,
    }));
    taken.forEach((i) => this.uploads.delete(i.localId));
    this._items.update((items) => items.filter((i) => !taken.includes(i)));
    this.clear();
    return attachments;
  }

  clear(): void {
    this._items().forEach((i) => this.remove(i.localId));
  }

  private start(localId: string): void {
    const item = this._items().find((i) => i.localId === localId);
    if (!item) return;

    this.uploads.get(localId)?.unsubscribe();
    this.uploads.set(
      localId,
      this.upload(item.file).subscribe({
        next: (event) => {
          if (event.type === 'progress') {
            this.patch(localId, {
              progress: event.total ? event.loaded / event.total : 0,
            });
          } else {
            this.patch(localId, {
              status: 'done',
              progress: 1,
              attachment: event.attachment,
            });
          }
        },
        error: (error: { code?: SupportErrorCode }) =>
          this.patch(localId, { status: 'failed', error: error?.code }),
      }),
    );
  }

  private patch(localId: string, changes: Partial<SupportDraftAttachment>): void {
    this._items.update((items) =>
      items.map((i) => (i.localId === localId ? { ...i, ...changes } : i)),
    );
  }
}

export function isImageType(mimeType: string): boolean {
  return /^image\/(png|jpe?g|gif|webp|avif)$/i.test(mimeType);
}

/** By MIME type, like the backend. Files with no type (e.g. .md) don't pass. */
function isAllowedFile(file: File): boolean {
  return SUPPORT_ATTACHMENT_LIMITS.mimeTypes.includes(file.type);
}

/* Shared by the chat composer and the issue report form. */

/** A translated line for the user (key + params). */
export interface SupportAttachmentNotice {
  key: string;
  params: Record<string, string | number>;
}

export function hasFiles(event: DragEvent): boolean {
  return !!event.dataTransfer?.types.includes('Files');
}

/** Clipboard images come in as "image.png"; give them a findable name. */
export function namePastedImage(file: File): File {
  if (!isImageType(file.type) || !/^image\.\w+$/i.test(file.name)) return file;
  const extension = file.type.split('/')[1].replace('jpeg', 'jpg');
  const stamp = new Date()
    .toISOString()
    .slice(0, 19)
    .replace(/[-:]/g, '')
    .replace('T', '-');
  return new File([file], `screenshot-${stamp}.${extension}`, {
    type: file.type,
  });
}

/** One line for the user, naming the first file that was left out. */
export function rejectionNotice(
  rejected: SupportAttachmentRejection[],
): SupportAttachmentNotice | null {
  const first = rejected[0];
  if (!first) return null;
  const { maxFiles, maxBytes } = SUPPORT_ATTACHMENT_LIMITS;
  switch (first.reason) {
    case 'type':
      return { key: 'support_chat.file_type_not_supported', params: { name: first.name } };
    case 'size':
      return {
        key: 'support_chat.file_too_large',
        params: { name: first.name, max: maxBytes / (1024 * 1024) },
      };
    default:
      return { key: 'support_chat.too_many_files', params: { max: maxFiles } };
  }
}

export function fileIcon(mimeType: string): string {
  if (isImageType(mimeType)) return 'image';
  if (mimeType === 'application/pdf') return 'picture_as_pdf';
  if (mimeType.startsWith('video/')) return 'movie';
  if (mimeType.startsWith('audio/')) return 'audiotrack';
  if (/spreadsheet|excel|csv|tab-separated/.test(mimeType)) return 'table_chart';
  if (/presentation|powerpoint/.test(mimeType)) return 'slideshow';
  return 'description';
}

export function uploadFailureKey(item: SupportDraftAttachment): string {
  if (item.error === 'RATE_LIMITED') return 'support_chat.upload_wait';
  if (item.error === 'INVALID_REQUEST') return 'support_chat.upload_rejected';
  return 'support_chat.upload_failed';
}

export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
